import styled from '@emotion/styled/macro';
import { Variables } from '../base/variables';
import { SidePanel } from '../messaging/side-panel';

const V = Variables.ThemeLight;

/**
 * Shared detail-panel primitives — ONE definition, in `base/detail-panel`.
 *
 * These were declared here and are entirely generic; nothing in them knows what a payer is.
 * The provider detail dialog needs the same section padding, type scale and label/value
 * alignment, and this file's own header records what happened the last time two panels each
 * owned their own copy: fourteen styled components that no longer matched.
 *
 * Re-exported rather than moved-and-rewired so `payer-drawer.tsx` needs no change at all —
 * it still does `import * as S from './payer-drawer.styles'` and gets the same names.
 */
export {
    Eyebrow,
    TitleRow,
    Title,
    Section,
    SectionHead,
    SectionTitle,
    SectionMeta,
    Card,
    Row,
    Text,
    Strong,
    Muted,
    Empty,
} from '../base/detail-panel/detail-panel.styles';

/**
 * The drawer, raised above MUI's modal layer.
 *
 * `SidePanel` sits at `z-index: 1200` and MUI's modals and popovers live at 1300. The drawer is
 * opened from a row action, which is a MUI Popover, and that popover's INVISIBLE full-viewport
 * backdrop stays mounted — so at 1200 the drawer renders on screen and is completely
 * unclickable. Close, and every other control, swallowed.
 *
 * Verified in a browser when this was first built: the close handler fired correctly when
 * dispatched on the node and could not be reached by an ordinary pointer click. Visible but
 * dead is the worst kind of failure — it looks like the feature works right up until someone
 * tries to use it, and a screenshot cannot tell the two apart.
 */
export const Panel = styled(SidePanel)`
    z-index: 1400;
`;

/** The drawer scrolls as one sheet; only the header stays put. */
export const Body = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${V.Background2};
`;

export const Header = styled.header`
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

export const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 5px;
    font: 500 11px/1.5 var(--font-sans, inherit);
    color: ${V.TextMedium};
    background: ${V.Background1};
    border: 1px solid ${V.Border1};
`;

/** Holds whatever the host app passes as `actions`. Empty and invisible when it passes none. */
export const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

/** `min-height: 0` is what actually lets a flex child scroll instead of growing its parent. */
export const Scroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 20px 28px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const Icon = styled.span`
    display: inline-flex;
    flex: none;
    margin-top: 2px;
    color: ${V.TextXMild};
`;

export const Lane = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 13px;
    border-bottom: 1px solid ${V.Border1};

    :last-of-type {
        border-bottom: none;
    }
`;

export const Table = styled.div`
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};
    overflow: hidden;
`;

export const TxRow = styled.div<{ head?: boolean }>`
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: ${({ head }) => (head ? '8px 13px' : '9px 13px')};
    border-bottom: 1px solid ${V.Border1};

    :last-of-type {
        border-bottom: none;
    }
`;

export const TxHead = styled.span`
    font: 600 10px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const TxName = styled.span<{ served: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font: 500 11px/1.4 var(--font-mono, monospace);
    color: ${({ served }) => (served ? V.TextStrong : V.TextXMild)};
`;

/** Truncates: a served-by name plus a clearinghouse name overflows this column readily. */
export const TxLabel = styled.span`
    font: 400 11px/1.4 var(--font-sans, inherit);
    color: ${V.TextMild};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Handles = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 8px;
`;

export const Handle = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: 5px;
    font: 500 10px/1.5 var(--font-sans, inherit);
    color: ${V.PrimaryInverted3};
    background: ${V.PrimaryMild2};
`;
